import {
    CalendarIcon,
    ClockIcon
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const HarvestWindow = () => {
  const { t } = useTranslation();

  const harvestMonths = [
    {
      month: t('harvestWindow.months.july'),
      status: t('harvestWindow.status.start'),
      description: t('harvestWindow.descriptions.start'),
      color: 'from-green-400 to-emerald-500',
    },
    {
      month: t('harvestWindow.months.august'),
      status: t('harvestWindow.status.peak'),
      description: t('harvestWindow.descriptions.peak'),
      color: 'from-avoprime-primary to-avoprime-secondary',
    },
    {
      month: t('harvestWindow.months.october'),
      status: t('harvestWindow.status.domestic'),
      description: t('harvestWindow.descriptions.domestic'),
      color: 'from-avoprime-primary to-avoprime-secondary',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <section className='py-8 bg-gradient-to-br from-avoprime-primary/5 to-avoprime-secondary/5'>
      <div className='container mx-auto px-4 sm:px-6 lg:px-8'>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className='text-center mb-16'
        >
          <h2 className='text-4xl sm:text-5xl lg:text-6xl font-eras font-bold text-avoprime-primary mb-6'>
            {t('harvestWindow.title')}
          </h2>
          <p className='text-xl lg:text-2xl font-montserrat text-gray-700 max-w-4xl mx-auto leading-relaxed'>
            {t('harvestWindow.subtitle')}
          </p>
        </motion.div>

        {/* Strategic Positioning */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className='bg-gradient-to-r from-avoprime-primary to-avoprime-secondary rounded-3xl p-8 lg:p-12 text-white mb-16'
        >
          <div className='grid lg:grid-cols-2 gap-8 items-center'>
            <div>
              <h3 className='text-3xl lg:text-4xl font-eras font-bold mb-6'>
                {t('harvestWindow.strategicPositioning.title')}
              </h3>
              <p className='text-lg lg:text-xl font-montserrat opacity-90 mb-6 leading-relaxed'>
                {t('harvestWindow.strategicPositioning.description')}
              </p>
              <div className='flex items-center space-x-4'>
                <div className='w-12 h-12 bg-white/20 rounded-full flex items-center justify-center'>
                  <ClockIcon className='w-6 h-6 text-white' />
                </div>
                <div>
                  <div className='font-eras font-bold text-lg'>
                    {t('harvestWindow.strategicPositioning.harvestPeriod')}
                  </div>
                  <div className='text-sm opacity-80'>
                    {t('harvestWindow.strategicPositioning.harvestPeriodDesc')}
                  </div>
                </div>
              </div>
            </div>
            <div className='relative'>
              <div
                className='w-full h-64 lg:h-80 rounded-2xl bg-cover bg-center'
                style={{
                  backgroundImage: "url('/avocados-secondary.webp')",
                }}
              />
              <div className='absolute inset-0 bg-gradient-to-t from-black/40 to-transparent rounded-2xl' />
            </div>
          </div>
        </motion.div>

        {/* Harvest Timeline */}
        <motion.div
          variants={containerVariants}
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          className='flex flex-col md:flex-row justify-center items-center gap-8 max-w-4xl mx-auto'
        >
          {harvestMonths.map((month, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className='relative group'
            >
              <div className='h-64 w-64 bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 flex flex-col'>
                <div
                  className={`w-16 h-16 bg-gradient-to-br ${month.color} rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}
                >
                  <CalendarIcon className='w-8 h-8 text-white' />
                </div>
                <h3 className='text-xl font-eras font-bold text-avoprime-primary mb-2 text-center'>
                  {month.month}
                </h3>
                <div className='text-base font-eras font-bold text-avoprime-secondary mb-2 text-center'>
                  {month.status}
                </div>
                <p className='text-sm text-gray-600 font-montserrat text-center flex-1 flex items-center justify-center leading-relaxed'>
                  {month.description}
                </p>
              </div>

            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default HarvestWindow;
